import { IComboBoxStyles } from '@fluentui/react/lib/ComboBox';
import { IRawStyle, ITheme } from '@fluentui/react/lib/Styling';
import { ContrastColors } from '../teams-colors';
import { FontSizes } from '../teams-type';
import { teamsBorderRadius, teamsButtonBorderRadius } from './constants';

export function getComboBoxStyles(
  themeName: string,
  theme: ITheme,
  background?: string
): Partial<IComboBoxStyles> {
  let ROOT_BACKGROUND_COLOR = theme.palette.neutralLight;
  let FIELD_GROUP_OPACITY = 0.36;

  let HOVER_COLOR = theme.palette.black;
  let HOVER_BACKGROUND_COLOR = theme.palette.neutralLight;
  let FOCUS_BACKGROUND_COLOR = theme.palette.neutralQuaternaryAlt;
  let KEYBOARD_FOCUS_BACKGROUND_COLOR = theme.palette.neutralQuaternaryAlt;
  let KEYBOARD_FOCUS_TEXT_COLOR = theme.palette.black;

  let FIELD_GROUP_BORDER_WIDTH = '0 0 2px 0';
  let FIELD_GROUP_BORDER_WIDTH_ACTIVE = FIELD_GROUP_BORDER_WIDTH;
  let FIELD_GROUP_BORDER_COLOR = 'transparent';
  let FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE = theme.palette.themePrimary;
  let FIELD_GROUP_BORDER_COLOR_DISABLED = 'transparent';
  let FIELD_COLOR_PLACEHOLDER_DISABLED = theme.palette.neutralDark;

  let PLACEHOLDER_COLOR = theme.palette.neutralDark;
  let PLACEHOLDER_ACTIVE_COLOR = theme.palette.neutralTertiary;

  if (themeName === 'dark') {
    ROOT_BACKGROUND_COLOR = theme.palette.white;
    HOVER_COLOR = theme.palette.black;
    HOVER_BACKGROUND_COLOR = theme.palette.neutralQuaternary;
    FOCUS_BACKGROUND_COLOR = theme.palette.neutralTertiaryAlt;

    PLACEHOLDER_COLOR = theme.palette.neutralDark;
    PLACEHOLDER_ACTIVE_COLOR = theme.palette.neutralSecondary;

    FIELD_COLOR_PLACEHOLDER_DISABLED = theme.palette.neutralPrimary;
  }

  if (themeName === 'contrast') {
    ROOT_BACKGROUND_COLOR = theme.palette.white;

    KEYBOARD_FOCUS_BACKGROUND_COLOR = theme.palette.themeDark;
    KEYBOARD_FOCUS_TEXT_COLOR = theme.palette.white;

    FIELD_GROUP_OPACITY = 1;
    FIELD_GROUP_BORDER_WIDTH_ACTIVE = '1px 1px 2px 1px';
    FIELD_GROUP_BORDER_WIDTH = '1px 1px 1px 1px';
    FIELD_GROUP_BORDER_COLOR = theme.palette.black;
    FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE = theme.palette.themeDark;
    FIELD_GROUP_BORDER_WIDTH_ACTIVE = '1px 1px 2px 1px';
    FIELD_GROUP_BORDER_COLOR_DISABLED = ContrastColors.appGreenContrast;
    FIELD_COLOR_PLACEHOLDER_DISABLED = ContrastColors.appGreenContrast;

    PLACEHOLDER_COLOR = theme.palette.black;
    PLACEHOLDER_ACTIVE_COLOR = theme.palette.black;
  }

  // If this combobox is on gray background
  if (background === 'gray') {
    ROOT_BACKGROUND_COLOR = theme.palette.white;
  }

  const isActive = {
    borderWidth: FIELD_GROUP_BORDER_WIDTH_ACTIVE,
    borderColor: FIELD_GROUP_BORDER_COLOR,
    borderBottomColor: FIELD_GROUP_BORDER_BOTTOM_COLOR_ACTIVE,
    borderBottomRightRadius: teamsButtonBorderRadius,
    borderBottomLeftRadius: teamsButtonBorderRadius
  };

  const styles = {
    divider: {
      margin: '4px 0',
      height: '2px',
      backgroundColor: theme.palette.neutralLight,
      border: 'none'
    },
    root: {
      borderRadius: teamsBorderRadius,
      paddingLeft: '12px',
      borderWidth: FIELD_GROUP_BORDER_WIDTH,
      borderStyle: 'solid',
      borderColor: FIELD_GROUP_BORDER_COLOR,
      background: ROOT_BACKGROUND_COLOR,
      color: theme.palette.black,
      minWidth: 'auto',
      selectors: {
        '&.is-open': {
          ...isActive
        },
        '.ms-Button--icon': {
          width: '34px',
          background: 'transparent',
          position: 'absolute',
          selectors: {
            ':hover': {
              color: 'inherit',
              background: 'transparent'
            }
          }
        },
        '.ms-Button-icon': {
          color: theme.palette.neutralPrimary,
          fontSize: FontSizes.size12,
          fontWeight: 'bold',
          position: 'relative',
          top: '2px'
        }
      }
    },
    rootError: {
      borderBottomRightRadius: teamsButtonBorderRadius,
      borderBottomLeftRadius: teamsButtonBorderRadius,
      borderWidth: FIELD_GROUP_BORDER_WIDTH_ACTIVE
    },
    input: {
      background: 'transparent',
      height: '30px',
      lineHeight: '30px',
      color: theme.palette.black,
      selectors: {
        '::placeholder': { color: PLACEHOLDER_COLOR },
        ':-ms-input-placeholder': { color: PLACEHOLDER_COLOR }
      }
    },
    inputDisabled: {
      color: theme.palette.black
    },
    rootPressed: {
      ...isActive
    },
    rootFocused: {
      ...isActive,
      selectors: {
        input: {
          selectors: {
            '::placeholder': { color: PLACEHOLDER_ACTIVE_COLOR },
            ':-ms-input-placeholder': { color: PLACEHOLDER_ACTIVE_COLOR }
          }
        }
      }
    },
    rootHovered: {
      borderColor: FIELD_GROUP_BORDER_COLOR,

      selectors: {
        '.ms-Button': {
          background: 'transparent'
        }
      }
    },
    rootDisabled: {
      opacity: FIELD_GROUP_OPACITY,
      borderColor: FIELD_GROUP_BORDER_COLOR_DISABLED,
      selectors: {
        input: {
          color: FIELD_COLOR_PLACEHOLDER_DISABLED
        },
        '.ms-Button-icon': {
          color: FIELD_COLOR_PLACEHOLDER_DISABLED
        }
      }
    },
    callout: {
      selectors: {
        '.ms-Callout-main': {
          paddingTop: '8px',
          paddingBottom: '8px'
        },
        '.is-checked': {
          background: KEYBOARD_FOCUS_BACKGROUND_COLOR,
          color: KEYBOARD_FOCUS_TEXT_COLOR,
          selectors: {
            ':after': {
              display: 'none'
            }
          }
        }
      }
    },
    header: {
      padding: '0 8px',
      color: theme.palette.neutralDark
    },
    optionsContainer: {
      selectors: {
        '.ms-Button': {
          paddingLeft: '15px',
          paddingRight: '15px',
          minHeight: 'auto',
          lineHeight: 'normal',
          whiteSpace: 'noWrap',
          background: 'transparent',
          color: theme.palette.neutralDark,
          selectors: {
            ':hover': {
              background: HOVER_BACKGROUND_COLOR,
              color: HOVER_COLOR
            },
            ':focus': {
              background: FOCUS_BACKGROUND_COLOR,
              color: HOVER_COLOR
            },
            ':active': {
              background: FOCUS_BACKGROUND_COLOR,
              color: HOVER_COLOR
            }
          }
        },
        '.ms-ComboBox-optionText': {
          overflow: 'visible'
        },
        '.ms-Checkbox-text': {
          color: 'inherit'
        },
        '.ms-Checkbox:hover': {
          selectors: {
            '.ms-Checkbox-text': {
              color: 'inherit',
              selectors: {
                ':hover': {
                  color: 'inherit'
                }
              }
            }
          }
        },
        '.is-disabled': {
          color: theme.palette.neutralTertiary
        }
      }
    }
  } as Partial<IComboBoxStyles>;

  // Dark theme overrides
  if (themeName === 'dark') {
    (styles.divider as IRawStyle).backgroundColor = theme.palette.white;

    ((styles.root as Required<IRawStyle>).selectors[
      '.ms-Button-icon'
    ] as IRawStyle).color = theme.palette.black;

    ((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.ms-Checkbox-text'
    ] as IRawStyle).color = theme.palette.neutralDark;
    (((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.ms-Checkbox:hover'
    ] as Required<IRawStyle>).selectors[
      '.ms-Checkbox-text'
    ] as IRawStyle).color = theme.palette.neutralDark;
    ((((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.ms-Checkbox:hover'
    ] as Required<IRawStyle>).selectors['.ms-Checkbox-text'] as Required<
      IRawStyle
    >).selectors[':hover'] as IRawStyle).color = theme.palette.neutralDark;
  }

  // Contrast theme overrides
  if (themeName === 'contrast') {
    (styles.divider as IRawStyle).backgroundColor = theme.palette.black;

    ((styles.root as Required<IRawStyle>).selectors[
      '.ms-Button-icon'
    ] as IRawStyle).color = theme.palette.black;

    ((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.ms-Checkbox-text'
    ] as IRawStyle).color = theme.palette.black;

    (((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.ms-Checkbox:hover'
    ] as Required<IRawStyle>).selectors[
      '.ms-Checkbox-text'
    ] as IRawStyle).color = theme.palette.black;
    ((((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.ms-Checkbox:hover'
    ] as Required<IRawStyle>).selectors['.ms-Checkbox-text'] as Required<
      IRawStyle
    >).selectors[':hover'] as IRawStyle).color = theme.palette.black;

    (((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.ms-Button'
    ] as Required<IRawStyle>).selectors[':hover'] as IRawStyle).color =
      theme.palette.white;
    (((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.ms-Button'
    ] as Required<IRawStyle>).selectors[':focus'] as IRawStyle).color =
      theme.palette.white;
    (((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.ms-Button'
    ] as Required<IRawStyle>).selectors[':active'] as IRawStyle).color =
      theme.palette.white;

    (((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.ms-Button'
    ] as Required<IRawStyle>).selectors[':hover'] as IRawStyle).background =
      theme.palette.themeDark;
    (((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.ms-Button'
    ] as Required<IRawStyle>).selectors[':focus'] as IRawStyle).background =
      theme.palette.themeDark;
    (((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.ms-Button'
    ] as Required<IRawStyle>).selectors[':active'] as IRawStyle).background =
      theme.palette.themeDark;

    ((styles.optionsContainer as Required<IRawStyle>).selectors[
      '.is-disabled'
    ] as IRawStyle).color = ContrastColors.appGreenContrast;
  }

  return styles;
}
